import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { authApi } from '../../services/api/authApi';
import { useAuthStore } from '../../store/authStore';

interface TwoFactorVerifyDialogProps {
  open: boolean;
  userId: number;
  email: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const TwoFactorVerifyDialog: React.FC<TwoFactorVerifyDialogProps> = ({
  open,
  userId,
  email,
  onClose,
  onSuccess,
}) => {
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setAuth, clear2FAState } = useAuthStore();

  const handleVerify = async () => {
    if (!code || (useBackupCode ? code.length < 8 : code.length !== 6)) {
      setError('Please enter a valid verification code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authApi.verify2FA({
        userId,
        code,
        useBackupCode,
      });

      // Set auth state with user and token
      setAuth(response.user, response.token);
      clear2FAState();
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCode('');
    setUseBackupCode(false);
    setError(null);
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && code.length > 0) {
      handleVerify();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Two-Factor Authentication</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {email}
        </Typography>

        <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
          {useBackupCode
            ? 'Enter one of your backup codes'
            : 'Enter the 6-digit code from your authenticator app'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label={useBackupCode ? 'Backup Code' : 'Verification Code'}
          value={code}
          onChange={(e) => {
            if (useBackupCode) {
              // Allow alphanumeric and dashes for backup codes
              const value = e.target.value.toUpperCase().replaceAll(/[^A-F0-9-]/g, '');
              setCode(value);
            } else {
              // Only allow digits for TOTP codes
              const value = e.target.value.replaceAll(/\D/g, '').slice(0, 6);
              setCode(value);
            }
          }}
          onKeyDown={handleKeyPress}
          placeholder={useBackupCode ? 'XXXX-XXXX' : '000000'}
          inputProps={{
            maxLength: useBackupCode ? 9 : 6,
            style: {
              textAlign: 'center',
              fontSize: '1.5rem',
              letterSpacing: useBackupCode ? '0.1rem' : '0.5rem',
              fontFamily: 'monospace',
            },
          }}
          autoFocus
        />

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              setUseBackupCode(!useBackupCode);
              setCode('');
              setError(null);
            }}
            sx={{ cursor: 'pointer' }}
          >
            {useBackupCode
              ? 'Use authenticator app instead'
              : 'Use backup code instead'}
          </Link>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleVerify}
          disabled={
            loading ||
            (useBackupCode ? code.length < 8 : code.length !== 6)
          }
        >
          {loading ? <CircularProgress size={24} /> : 'Verify'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
